import { motion } from "framer-motion";
import { useMemo } from "react";
import type { Car, ServiceHistory, ShftService, User } from "@/types/database";
import { getUserPlatformCharts, getUserPlatformOverview } from "@/lib/user-analytics";
import { LazyUserCharts } from "@/components/users/UserChartsClient";
import { MetricCard } from "@/components/ui/Card";
import { Workspace, WorkspaceSection } from "@/components/ui/Workspace";
import { formatDate, formatNumber } from "@/lib/utils";

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] as const },
  },
};

export function UserAnalytics({
  users,
  cars,
  serviceHistory,
  shftServices,
}: {
  users: User[];
  cars: Car[];
  serviceHistory: ServiceHistory[];
  shftServices: ShftService[];
}) {
  const overview = useMemo(
    () => getUserPlatformOverview(users, cars, serviceHistory, shftServices),
    [users, cars, serviceHistory, shftServices]
  );
  const charts = useMemo(
    () => getUserPlatformCharts(users, cars, serviceHistory, shftServices),
    [users, cars, serviceHistory, shftServices]
  );

  return (
    <motion.div initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.08 } } }}>
      <motion.div variants={itemVariants}>
        <Workspace>
          <WorkspaceSection className="p-4 sm:p-5">
            <div className="mb-5">
              <h2 className="font-bold text-foreground">User insights</h2>
              <p className="mt-1 text-sm text-muted">
                Platform growth, ownership, and service activity across Shift users.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <MetricCard label="Total users" value={formatNumber(overview.totalUsers)} className="p-4" />
              <MetricCard label="Admins" value={formatNumber(overview.adminCount)} className="p-4" />
              <MetricCard
                label="Avg cars / user"
                value={overview.avgCarsPerUser.toFixed(1)}
                className="p-4"
              />
              <MetricCard label="Total services" value={formatNumber(overview.totalServices)} className="p-4" />
            </div>

            {overview.latestSignup ? (
              <p className="mt-4 text-xs text-muted">
                Latest sign-up:{" "}
                <span className="font-semibold text-foreground">{formatDate(overview.latestSignup)}</span>
              </p>
            ) : null}
          </WorkspaceSection>

          <WorkspaceSection border className="p-4 sm:p-5">
            <LazyUserCharts charts={charts} />
          </WorkspaceSection>
        </Workspace>
      </motion.div>
    </motion.div>
  );
}
